import Link from "next/link";
import styled from "styled-components";
import Rating from "lib/components/Rating";
import { SelectOption } from "lib/components/Select";
import useIsResMobile from "lib/hooks/useIsResMobile";
import IconPromotion from "lib/assets/promotion.svg";
import { useEvent } from "lib/contexts/eventContext";
import { CustomEventLabel } from "lib/enums";
import { useLanguageName, useTranslations } from "lib/i18n";
import { usePracticeArea } from "domain/consultation-request/usePracticeArea";

interface CardLawyerProps {
  lawyer: ResLawyerProfile;
  availableLanguages: SelectOption[];
  className?: string;
}

const CardLawyer: React.FC<CardLawyerProps> = ({
  lawyer,
  availableLanguages,
  className,
}) => {
  const t = useTranslations("lawyers");
  const languageName = useLanguageName();
  const practiceArea = usePracticeArea();
  const { tag } = useEvent();
  const isResMobile = useIsResMobile();
  const reviewRating = Math.round(lawyer.averageStarRate * 100) / 100; // 3.33333 -> 3.33
  const languages = lawyer.languages.map((languageCode) => {
    const selectedLanguageCode = availableLanguages.find(
      (code) => code.value === languageCode,
    );
    return languageName(
      languageCode,
      selectedLanguageCode?.label ?? languageCode,
    );
  });

  return (
    <Link href={`/lawyers/${lawyer.urlSlug}/`} passHref>
      <LawyerLink
        className={lawyer.isOnline ? "CardLawyer_Available_Now" : "CardLawyer"}
        onClick={() =>
          tag?.(CustomEventLabel.FindALawyerLawyerListProfileArea, {
            name: lawyer.lawyerName,
            type: lawyer.isOnline ? "live" : "booking",
            price: lawyer.defaultPaymentFee.attorneyFee,
          })
        }
      >
        <Wrapper
          css={`
            padding: ${isResMobile ? 30 : 40}px 0;
            align-items: ${isResMobile ? `flex-start` : `center`};
          `}
          className={className}
        >
          {/* left: picture section */}
          <div
            css={`
              width: ${isResMobile ? 60 : 180}px;
              height: ${isResMobile ? 60 : 180}px;
              border-radius: 50%;
              position: relative;
              overflow: hidden;
              margin-right: ${isResMobile ? 12 : 40}px;
              background-color: #f1f3f5;
            `}
          >
            <img
              css={`
                width: 100%;
                height: auto;
              `}
              src={lawyer.originalProfileImageUrl || lawyer.profileImageUrl}
              alt=""
            />
          </div>

          {/* right: info section */}
          <div
            css={`
              position: relative;
              flex: 1;
              align-items: flex-start;
              flex-direction: column;
            `}
          >
            {lawyer.isOnline && <BadgeLive>Live Consultation</BadgeLive>}
            {/* name / rating / review count */}
            <div
              css={`
                position: relative;
                flex: 1;
                display: flex;
                align-items: ${isResMobile ? `flex-start` : `center`};
                flex-direction: ${isResMobile ? `column` : `row`};
                flex-wrap: wrap;
              `}
            >
              <Name
                className="lawyerName"
                css={`
                  margin-right: 10px;
                  font-size: ${isResMobile ? 20 : 24}px;
                  line-height: ${isResMobile ? 24 : 32}px;
                  order: 1;
                  padding-right: ${isResMobile ? 20 : 0}px;
                `}
              >
                {lawyer.lawyerName}
              </Name>
              <div
                css={`
                  display: flex;
                  order: ${isResMobile ? 3 : 2};
                  align-items: center;
                `}
              >
                <Rating rating={reviewRating} hideText gutter={3} size={13.5} />
                <ReviewCount
                  css={`
                    margin-left: 8px;
                  `}
                >
                  {t("card.reviews", { count: lawyer.numOfReviews })}
                </ReviewCount>
              </div>

              <Rate
                isPromoted={
                  !!lawyer.defaultPaymentFee.previousPromotionAttorneyFee
                }
                origPrice={
                  lawyer.defaultPaymentFee.previousPromotionAttorneyFee
                }
                css={`
                  position: absolute;
                  right: 0;
                  top: 0;
                  display: block;
                `}
              >
                <>
                  {`$${lawyer.defaultPaymentFee.attorneyFee}`}
                  <span
                    css={`
                      display: ${isResMobile ? `none` : `inline`};
                    `}
                  >
                    {" "}
                    {t("card.perMinutes")}
                  </span>
                </>
              </Rate>

              {/* address & languages */}
              <div
                css={`
                  flex-basis: 100%;
                  margin: ${isResMobile ? `6px 0 10px` : `2px 0 15px`};
                  flex-direction: column;
                  order: ${isResMobile ? 2 : 3};
                `}
              >
                <FirmName
                  css={`
                    font-size: ${isResMobile ? 13 : 16}px;
                    margin: ${isResMobile ? `0 0 5px 0` : `0 0 10px 0`};
                  `}
                >
                  {lawyer.firmName}
                </FirmName>
                <Languages>{languages.join(`, `)}</Languages>
              </div>
            </div>

            {/* Practice areas */}
            <PracticeAreas>
              <div
                css={`
                  overflow: hidden;
                  display: ${isResMobile ? `none` : `block`};
                  flex-basis: 100%;
                `}
              >
                {lawyer.specialityTypes.slice(0, 4).map((type) => (
                  <Label key={type.typeId}>{practiceArea.name(type)}</Label>
                ))}
                {lawyer.specialityTypes.length > 4 && (
                  <Label>
                    {t("card.more", {
                      count: lawyer.specialityTypes.length - 4,
                    })}
                  </Label>
                )}
              </div>
              <PracticeAreasComplete
                className="reveal"
                css={`
                  flex-basis: 100%;
                  overflow: hidden;
                  display: ${isResMobile ? `none` : `block`};
                `}
              >
                {lawyer.specialityTypes.map((type) => (
                  <Label key={type.typeId}>{practiceArea.name(type)}</Label>
                ))}
              </PracticeAreasComplete>
            </PracticeAreas>
          </div>
        </Wrapper>
        {lawyer.isOnline && (
          <ButtonLive
            css={`
              display: ${isResMobile ? `block` : `none`};
              padding: 15px 20px;
              margin-bottom: 20px;
            `}
          >
            Available Now
          </ButtonLive>
        )}
      </LawyerLink>
    </Link>
  );
};

export default CardLawyer;

const Wrapper = styled.li`
  border-bottom: 1px solid ${(props) => props.theme.palette.grey[100]};
  display: flex;
  color: ${(props) => props.theme.palette.text.primary};

  & .overlay {
    opacity: 0;
    transition: opacity 0.2s linear;
  }

  &:hover .overlay {
    opacity: 0.08;
  }

  &:hover .lawyerName {
    color: #195dee;
  }
`;

const Name = styled.div`
  transition: color 0.2s linear;
  font-weight: bold;
`;

const ReviewCount = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: #777777;
`;

const FirmName = styled.div`
  font-weight: 500;
  line-height: 22px;
`;

const Languages = styled.div`
  font-weight: normal;
  font-size: 12px;
  line-height: 18px;
  color: #777777;
`;

const RateText = styled.div<{ isPromoted?: boolean }>`
  font-size: 18px;
  line-height: 24px;
  color: ${({ isPromoted }) => (isPromoted ? "#ff6666" : "#000000")};
  font-weight: bold;
  display: inline;
  margin-left: 4px;
`;

const RateTextOrig = styled.div`
  text-align: right;
  text-decoration-line: line-through;
  color: #979797;
  font-weight: 500;
  font-size: 16px;
  margin-top: 2px;
`;

const Rate: React.FC<{
  isPromoted?: boolean;
  className?: string;
  origPrice: number | null;
}> = ({ isPromoted, children, className, origPrice }) => (
  <div className={className}>
    <div
      css={`
        display: flex;
        align-items: center;
      `}
    >
      {isPromoted && <IconPromotion />}
      <RateText isPromoted={isPromoted}>{children}</RateText>
    </div>
    {isPromoted && origPrice && <RateTextOrig>{`$${origPrice}`}</RateTextOrig>}
  </div>
);

const PracticeAreas = styled.div`
  position: relative;

  &:hover .reveal {
    transition: opacity 0.3s linear, visibility 0s linear;
    visibility: visible;
    opacity: 1;
  }
`;

const PracticeAreasComplete = styled.div`
  padding: 20px;
  top: -20px;
  left: -20px;
  z-index: 1;
  position: absolute;
  background: #ffffff;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  transition: opacity 0.3s linear, visibility 0s linear 0.3s;
  visibility: hidden;
  opacity: 0;

  &:hover {
    visibility: visible;
    opacity: 1;
  }
`;

const Label = styled.div`
  display: inline-block;
  padding: 5px 10px;
  background-color: #f3f4f8;
  color: black;
  font-size: 14px;
  border-radius: 10px;
  margin-right: 5px;
  margin-bottom: 5px;
`;

const BadgeLive = styled.div`
  display: inline-block;
  background: #00194d;
  border-radius: 14px;
  font-weight: 500;
  font-size: 10px;
  line-height: 10px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #ffffff;
  padding: 6px 8px;
  flex-basis: auto;
  margin-bottom: 10px;
`;

const ButtonLive = styled.button`
  background: #00194d;
  border-radius: 30px;
  color: white;
  width: 100%;
  font-weight: 500;
  font-size: 16px;
  cursor: pointer;
`;

const LawyerLink = styled.a`
  &:last-of-type ${Wrapper} {
    border-bottom: none;
  }
`;
